import React, { useMemo } from 'react';

import { Box, FormControl, FormControlLabel, RadioGroup, Typography } from '@material-ui/core';
import { Controller, useFormContext } from 'react-hook-form';
import { FiSave, FiTrash2 } from 'react-icons/fi';

import { Team } from '@vyce/core/src/types';
import { AppCheckbox, AppSwitch, AppTextField } from '@vyce/core/src/components/inputs';
import { AppRoundedRadio } from '@vyce/core/src/components/inputs/AppRadio';
import { ControlledSelect, ControlledTagAutocomplete } from '@vyce/core/src/components/controlled-inputs';
import { AppButton } from '@vyce/core/src/components/AppButton';
import { SettingsCard } from '@vyce/core/src/components/SettingsCard';
import { ExpiringDocumentType } from '@vyce/core/src/api/types';

import { StaffItem } from '../../../time/TimeNotifications/hooks/useStaffSearch';
import { DOCUMENT_TYPES, FREQUENCY_OPTIONS, HOUR_ITEMS, MONTH_DAY_ITEMS, WEEKDAY_ITEMS } from '../constants';
import { OptionItem } from '../types';

interface Props {
  index: number;
  teams: Team[];
  staff?: StaffItem[];
  setStaffSubstring?: (substring: string) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onSave: (index: number) => void;
  onDelete: (index: number) => void;
  onToggle: (index: number, enabled: boolean) => void;
  savingIndex?: number | null;
  deletingIndex?: number | null;
  boldClassName?: string;
}

export const ExpiringDocumentsCard: React.FC<Props> = ({
  index,
  teams,
  staff = [],
  setStaffSubstring,
  expanded,
  onExpandedChange,
  onSave,
  onDelete,
  onToggle,
  savingIndex = null,
  deletingIndex = null,
  boldClassName = '',
}) => {
  const fieldPrefix = `notifications.${index}` as const;
  const { control, getValues, setValue, watch } = useFormContext();

  const uuid: string | undefined = watch(`${fieldPrefix}.uuid`);
  const name: string = watch(`${fieldPrefix}.name`) || '';
  const enabled: boolean = watch(`${fieldPrefix}.enabled`) ?? false;
  const selectedTeamIds: string[] = watch(`${fieldPrefix}.teamIds`) || [];
  const selectedDocumentTypes: ExpiringDocumentType[] = watch(`${fieldPrefix}.documentTypes`) || [];
  const frequency = watch(`${fieldPrefix}.frequency`);
  const dayOfWeek: OptionItem | null = watch(`${fieldPrefix}.dayOfWeek`);
  const dayOfMonth: OptionItem | null = watch(`${fieldPrefix}.dayOfMonth`);
  const sendHour: OptionItem | null = watch(`${fieldPrefix}.sendHour`);
  const receivers: OptionItem[] = watch(`${fieldPrefix}.receivers`) || [];

  const saving = savingIndex === index;
  const deleting = deletingIndex === index;
  // A brand-new row exists only in the form until the first Save — nothing is scheduled yet.
  const saved = !!uuid;

  // The one-line recap in the tile header, so the setup can be read without opening it.
  const summary = useMemo(() => {
    const parts: string[] = [];

    if (frequency === 'weekly') parts.push(dayOfWeek ? `Weekly on ${dayOfWeek.name}` : 'Weekly');
    else if (frequency === 'specific_day')
      parts.push(dayOfMonth ? `Monthly on day ${dayOfMonth.name}` : 'Monthly');
    else parts.push('Daily');

    if (sendHour) parts.push(`at ${sendHour.name}`);

    const allTeams = teams.length > 0 && selectedTeamIds.length === teams.length;
    parts.push(
      allTeams ? 'all teams' : `${selectedTeamIds.length} ${selectedTeamIds.length === 1 ? 'team' : 'teams'}`
    );

    const allDocs = selectedDocumentTypes.length === DOCUMENT_TYPES.length;
    parts.push(
      allDocs
        ? 'all documents'
        : DOCUMENT_TYPES.filter(doc => selectedDocumentTypes.includes(doc.value))
            .map(doc => doc.name)
            .join(', ') || 'no documents'
    );

    parts.push(
      receivers.length
        ? `${receivers.length} ${receivers.length === 1 ? 'recipient' : 'recipients'}`
        : 'no recipients yet'
    );

    return parts.join(' · ');
  }, [
    frequency,
    dayOfWeek,
    dayOfMonth,
    sendHour,
    teams.length,
    selectedTeamIds,
    selectedDocumentTypes,
    receivers.length,
  ]);

  const toggleTeam = (uuid: string) => {
    const ids: string[] = getValues(`${fieldPrefix}.teamIds`) || [];
    const next = ids.includes(uuid) ? ids.filter(id => id !== uuid) : [...ids, uuid];
    setValue(`${fieldPrefix}.teamIds`, next);
  };

  const toggleAllTeams = () => {
    const ids: string[] = getValues(`${fieldPrefix}.teamIds`) || [];
    const allIds = teams.map(t => t.uuid);
    const next = ids.length === teams.length && teams.length > 0 ? [] : allIds;
    setValue(`${fieldPrefix}.teamIds`, next);
  };

  const toggleDocumentType = (value: ExpiringDocumentType) => {
    const types: ExpiringDocumentType[] = getValues(`${fieldPrefix}.documentTypes`) || [];
    const next = types.includes(value) ? types.filter(t => t !== value) : [...types, value];
    setValue(`${fieldPrefix}.documentTypes`, next);
  };

  return (
    <SettingsCard
      title={name.trim() || 'Untitled notification'}
      description={summary}
      status={saved ? (enabled ? 'On' : 'Off') : 'Not saved'}
      statusTone={saved ? (enabled ? 'on' : 'off') : 'info'}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      keepMounted
      nested
      headerControl={
        <AppSwitch
          checked={enabled}
          onChange={(_, checked) => onToggle(index, checked)}
          inputProps={{ 'aria-label': `${name.trim() || 'Untitled notification'} enabled` }}
        />
      }
      actions={
        <>
          <AppButton
            variant="outlined"
            color="primary"
            startIcon={<FiTrash2 size={16} />}
            onClick={() => onDelete(index)}
            disabled={saving || deleting}
            loading={deleting}
            loaderVariant="primary"
          >
            Delete
          </AppButton>
          <AppButton
            variant="contained"
            color="primary"
            startIcon={<FiSave size={16} />}
            onClick={() => onSave(index)}
            disabled={saving || deleting}
            loading={saving}
            loaderVariant="primary"
          >
            Save changes
          </AppButton>
        </>
      }
    >
      <Box display="flex" flexDirection="column" gridGap={24}>
        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            Notification name
          </Typography>
          <Controller
            name={`${fieldPrefix}.name`}
            control={control}
            rules={{ required: true, maxLength: 127 }}
            render={({ field }) => (
              <AppTextField
                {...field}
                label="e.g. Weekly — Site managers"
                fullWidth
                margin="none"
                inputProps={{ maxLength: 127 }}
              />
            )}
          />
        </Box>

        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            Which teams should be included?
          </Typography>
          <Box display="flex" flexDirection="column" gridGap={8}>
            <Box display="flex" alignItems="center" gridGap={8}>
              <AppCheckbox
                checked={selectedTeamIds.length === teams.length && teams.length > 0}
                onChange={toggleAllTeams}
                variant="rectangle"
                color="primary"
              />
              <Typography variant="body2">All Teams</Typography>
            </Box>
            {teams.map(team => (
              <Box key={team.uuid} display="flex" alignItems="center" gridGap={8}>
                <AppCheckbox
                  checked={selectedTeamIds.includes(team.uuid)}
                  onChange={() => toggleTeam(team.uuid)}
                  variant="rectangle"
                  color="primary"
                />
                <Typography variant="body2">{team.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            Which documents should be included?
          </Typography>
          <Box display="flex" flexDirection="column" gridGap={8}>
            {DOCUMENT_TYPES.map(doc => (
              <Box key={doc.value} display="flex" alignItems="center" gridGap={8}>
                <AppCheckbox
                  checked={selectedDocumentTypes.includes(doc.value)}
                  onChange={() => toggleDocumentType(doc.value)}
                  variant="rectangle"
                  color="primary"
                />
                <Typography variant="body2">{doc.name}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            How often should the email be sent?
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              aria-label="frequency"
              value={frequency}
              onChange={e => setValue(`${fieldPrefix}.frequency`, e.target.value)}
            >
              {FREQUENCY_OPTIONS.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<AppRoundedRadio color="primary" />}
                  label={opt.name}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Box>

        {frequency === 'weekly' && (
          <Box>
            <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
              Which day should the email be sent?
            </Typography>
            <ControlledSelect
              name={`${fieldPrefix}.dayOfWeek`}
              items={WEEKDAY_ITEMS}
              label="Select day"
              multiple={false}
            />
          </Box>
        )}

        {frequency === 'specific_day' && (
          <Box>
            <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
              Which day of the month should the email be sent?
            </Typography>
            <ControlledSelect
              name={`${fieldPrefix}.dayOfMonth`}
              items={MONTH_DAY_ITEMS}
              label="Select day of month"
              multiple={false}
            />
          </Box>
        )}

        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            What time should the email be sent? Notifications are sent out on the hour.
          </Typography>
          <Box maxWidth={200}>
            <ControlledSelect
              name={`${fieldPrefix}.sendHour`}
              items={HOUR_ITEMS}
              label="Select hour"
              multiple={false}
            />
          </Box>
        </Box>

        <Box>
          <Typography className={boldClassName} variant="caption" display="block" gutterBottom>
            Who to?
          </Typography>
          <ControlledTagAutocomplete
            name={`${fieldPrefix}.receivers`}
            items={staff}
            label="Select recipients"
            multiple
            setSubstring={setStaffSubstring}
            margin="none"
          />
        </Box>
      </Box>
    </SettingsCard>
  );
};
